// report.service.ts
import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import * as XLSX from 'xlsx';

export interface ReportParams {
  fecha_inicio: string;
  fecha_fin: string;
  incluir_detalle: boolean;
}

export interface ReportResponse {
  resumen: any;
  estadisticas_unidrogas: any;
  detalle: any[];
}

// === INTERFACES PARA FILTROS EAN ===
export interface Producto {
  ean_code: string;
  product_name: string;
}

export interface Coleccion {
  id?: number;
  name: string;
  description: string;
  productos: Producto[];
  created_at?: string;
  updated_at?: string;
}

export interface ColeccionResponse {
  id: number;
  name: string;
  description: string;
  productos: Producto[];
  total_codes?: number; // Nueva propiedad del API
  created_at: string;
  updated_at: string;
}

export interface ProductoDisponible {
  ean_code: string;
  product_name: string;
  maker?: string;
  provider?: string;
}

export interface ColeccionApiResponse {
  data?: ColeccionResponse[];
  colecciones?: ColeccionResponse[];
  results?: ColeccionResponse[];
  filtros?: any[];
}

// Possible API response formats for products
export interface ProductoApiResponse {
  data?: ProductoDisponible[];
  productos?: ProductoDisponible[];
  results?: ProductoDisponible[];
}

@Injectable({
  providedIn: 'root'
})
export class ReportService {
  constructor(private _http: HttpClient) { }

  // === MÉTODOS PARA FILTROS EAN ===

  /**
   * Obtiene la lista de todas las colecciones EAN
   */
  listarColeccionesEan(): Observable<any> {
    return this._http.get<any>(environment.Api_filtros_ean_listar);
  }

  /**
   * Obtiene los detalles de una colección EAN específica con sus productos
   * @param id ID de la colección
   */
  obtenerColeccionEan(id: number): Observable<ColeccionResponse> {
    return this._http.get<ColeccionResponse>(`${environment.Api_filtros_ean_detalle}${id}/`);
  }

  /**
   * Crea una nueva colección EAN con productos
   * @param coleccion Datos de la colección a crear
   */
  crearColeccionEan(coleccion: Coleccion): Observable<any> {
    return this._http.post<any>(environment.Api_filtros_ean_crear, coleccion);
  }

  /**
   * Actualiza los productos de una colección EAN existente
   * @param id ID de la colección
   * @param productos Lista de productos a reemplazar
   */
  actualizarColeccionEan(id: number, productos: Producto[]): Observable<any> {
    const body = { productos };
    return this._http.post<any>(`${environment.Api_filtros_ean_actualizar}${id}/actualizar/`, body);
  }

  /**
   * Busca productos disponibles por nombre para filtros EAN
   * @param nombre Nombre del producto a buscar
   */
  buscarProductosEan(nombre: string = ''): Observable<any> {
    const url = nombre
      ? `${environment.Api_filtros_ean_productos}?name=${encodeURIComponent(nombre)}`
      : environment.Api_filtros_ean_productos;

    return this._http.get<any>(url);
  }

  /**
   * Elimina una colección EAN
   * @param id ID de la colección a eliminar
   */
  eliminarColeccionEan(id: number): Observable<any> {
    return this._http.delete<any>(`${environment.Api_filtros_ean_detalle}${id}/`);
  }

  // === MÉTODOS EXISTENTES ===

  // Método para exportar a Excel
  exportToExcel(data: any[], fileName: string): void {
    if (!data || data.length === 0) {
      console.warn('No hay datos para exportar');
      return;
    }

    // Crear un nuevo libro de trabajo
    const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet(data);
    const wb: XLSX.WorkBook = XLSX.utils.book_new();

    // Agregar la hoja al libro
    XLSX.utils.book_append_sheet(wb, ws, 'Reporte');

    // Ajustar el ancho de las columnas automáticamente
    const colWidths = this.getColumnWidths(data);
    ws['!cols'] = colWidths;

    // Generar el archivo y descargarlo
    const timestamp = new Date().toISOString().slice(0, 10);
    const fullFileName = `${fileName}_${timestamp}.xlsx`;
    XLSX.writeFile(wb, fullFileName);
  }

  // Método auxiliar para calcular el ancho de columnas
  private getColumnWidths(data: any[]): any[] {
    if (!data || data.length === 0) return [];

    const keys = Object.keys(data[0]);
    return keys.map(key => {
      let maxLength = key.length; // Longitud del header

      // Encontrar la longitud máxima en los datos
      data.forEach(row => {
        const cellLength = row[key] ? row[key].toString().length : 0;
        if (cellLength > maxLength) {
          maxLength = cellLength;
        }
      });

      return { width: Math.min(maxLength + 2, 50) }; // Máximo 50 caracteres
    });
  }

  // Nuevos métodos para los reportes de prescripción
  getUnidrogasReport(params: any): Observable<any> {
    let httpParams = new HttpParams()
      .set('fecha_inicio', params.fecha_inicio)
      .set('fecha_fin', params.fecha_fin)
      .set('incluir_detalle', params.incluir_detalle.toString());

    // Solo agregar el parámetro orden_fecha si no está vacío
    if (params.orden_fecha && params.orden_fecha.trim() !== '') {
      httpParams = httpParams.set('orden_fecha', params.orden_fecha);
    }

    // Solo agregar el parámetro filtro_ean_id si no está vacío
    if (params.filtro_ean_id && params.filtro_ean_id.toString().trim() !== '') {
      httpParams = httpParams.set('filtro_ean_id', params.filtro_ean_id.toString());
    }

    return this._http.get<any>(
      environment.comparar_prescripcion_unidrogas, // Asume que tienes esta URL en environment
      { params: httpParams }
    );
  }

  getCompraReport(params: any): Observable<any> {
    let httpParams = new HttpParams()
      .set('fecha_inicio', params.fecha_inicio)
      .set('fecha_fin', params.fecha_fin)
      .set('incluir_detalle', params.incluir_detalle.toString());

    // Solo agregar el parámetro fuente si no está vacío (para permitir "todas las fuentes")
    if (params.fuente && params.fuente.trim() !== '') {
      httpParams = httpParams.set('fuente', params.fuente);
    }

    return this._http.get<any>(
      environment.comparar_prescripcion_compra,
      { params: httpParams }
    );
  }
  // Métodos existentes
  getMedics(params: any) {
    return this._http.get(environment.ListMedicsGeneral, { params });
  }

  getMedicComercial() {
    return this._http.get(environment.ListarMedicComercial);
  }

  getViewFormulaAll(page, search) {
    let params = '?page=' + page + (search ? '&search=' + search : '');
    return this._http.get(environment.viewFormula + params);
  }

  getPatients() {
    return this._http.get(environment.allPatient);
  }

  getMedicFormulaPatient(id) {
    let params = '/' + id;
    return this._http.get(environment.formulaPatient + params);
  }

  getComercialRetail(start, end, search) {
    let params =
      (start ? '?start=' + encodeURIComponent(start) : '') +
      (end ? '&end=' + encodeURIComponent(end) : '') +
      (search ? '?search=' + search : '');
    return this._http.get(environment.ComercialRetail + params);
  }

  getAdminRetail(start, end, search) {
    let params =
      (start ? '?start=' + encodeURIComponent(start) : '') +
      (end ? '&end=' + encodeURIComponent(end) : '') +
      (search ? '?search=' + search : '');

    return this._http.get(environment.administradorRetail + params);
  }

  getProductList(
    page,
    product__name,
    product__ean_code,
    product__maker,
    provider
  ) {
    let params =
      '?page=' +
      page +
      (product__name ? '&product__name=' + product__name : '') +
      (product__ean_code ? '&product__ean_code=' + product__ean_code : '') +
      (product__maker ? '&product__maker=' + product__maker : '') +
      (provider ? '&provider=' + provider : '');
    return this._http.get(environment.ProductList + params);
  }

  getAdminProductList(
    page,
    product__name,
    product__ean_code,
    product__maker,
    provider,
    start,
    end
  ) {
    let params =
      '?page=' +
      page +
      (product__name ? '&product__name=' + product__name : '') +
      (product__ean_code ? '&product__ean_code=' + product__ean_code : '') +
      (product__maker ? '&product__maker=' + product__maker : '') +
      (provider ? '&provider=' + provider : '') +
      (start ? '&start=' + encodeURIComponent(start) : '') +
      (end ? '&end=' + encodeURIComponent(end) : '');
    return this._http.get(environment.adminProductList + params);
  }

  getComercialNotUsed(page, start, end, search) {
    let params =
      '?page=' +
      page +
      (start ? '&start=' + encodeURIComponent(start) : '') +
      (end ? '&end=' + encodeURIComponent(end) : '') +
      (search ? '&search=' + search : '');
    return this._http.get(environment.ComercialNotUsed + params);
  }

  getAdminNotUsed(page, start, end, search) {
    let params =
      '?page=' +
      page +
      (start ? '&start=' + encodeURIComponent(start) : '') +
      (end ? '&end=' + encodeURIComponent(end) : '') +
      (search ? '&search=' + search : '');

    return this._http.get(environment.administradorNotUsed + params);
  }

  getMedicPatient(id, start, end, search) {
    let params =
      id +
      '/' +
      (start ? '?start=' + encodeURIComponent(start) : '') +
      (end ? '&end=' + encodeURIComponent(end) : '') +
      (search ? '?search=' + search : '');

    return this._http.get(environment.ListarMedicAndPatiendAdmin + params);
  }

  getPointReport(medic_id) {
    let params = medic_id ? '?medic_id=' + medic_id : '';

    return this._http.get(environment.ListarPointReport + params);
  }

  getAdminPointReport(medic_id, start, end) {
    let params = medic_id
      ? '?medic_id=' + medic_id
      : '' +
      (start ? '&start=' + encodeURIComponent(start) : '') +
      (end ? '&end=' + encodeURIComponent(end) : '');

    return this._http.get(environment.administradorPointReport + params);
  }

  getMedicRedemption(page, start, end) {
    let params =
      '?page=' +
      page +
      (start ? '&start=' + encodeURIComponent(start) : '') +
      (end ? '&end=' + encodeURIComponent(end) : '');
    return this._http.get(environment.medicRedemption + params);
  }

  getFormulaMedic(page) {
    let params = '?page=' + page;
    return this._http.get(environment.formulaMedicReport + params);
  }

  getComercialFormulaMedic(page) {
    let params = '?page=' + page;
    return this._http.get(environment.comercialMedicReport + params);
  }

  getComercialREdemption(page, start, end) {
    let params =
      '?page=' +
      page +
      (start ? '&start=' + encodeURIComponent(start) : '') +
      (end ? '&end=' + encodeURIComponent(end) : '');
    return this._http.get(environment.comercialRedemption + params);
  }

  eficiencyStat(start, end) {
    const body = { start: start, end: end };

    return this._http.post(environment.efficiency, body);
  }
}
